import React, { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import usePost from "../../Hooks/usePost";
import { Row, Col, Spinner } from "reactstrap";
import { dangerMessage, successMessage } from "../../components/Toast/Toast";

const AddFAQ = ({ eventID, token, toggleAddFAQ, onFaqAdded }) => {
  const queryKey = ["EventDetails", eventID];
  const [inputs, setInputs] = useState({ question: "", answer: "" });
  const [validationErrors, setValidationErrors] = useState({});
  const [hasShownSuccess, setHasShownSuccess] = useState(false); // ✅ Track if we showed success
  
  const queryClient = useQueryClient();
  const { status, execute, pending, error, reset } = usePost();

  const validateForm = () => {
    const errors = {};
    
    if (!inputs?.question?.trim()) {
      errors.question = "Question is required";
    } else if (inputs.question.trim().length < 5) {
      errors.question = "Question must be at least 5 characters";
    }
    
    if (!inputs?.answer?.trim()) {
      errors.answer = "Answer is required";
    } else if (inputs.answer.trim().length < 10) {
      errors.answer = "Answer must be at least 10 characters";
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateFaq = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      dangerMessage("Please fill in all required fields correctly.");
      return;
    }
    
    if (!eventID) {
      dangerMessage("Event ID is missing. Cannot add FAQ.");
      return;
    }

    const endPoint = `faq/${eventID}`;
    const raw = JSON.stringify({
      question: inputs.question.trim(),
      answer: inputs.answer.trim(),
    });
    
    try {
      // ✅ Don't pass success message to execute - we'll handle it ourselves
      await execute(endPoint, raw, "POST", null, token);
    } catch (err) {
      console.error("❌ Request failed:", err);
    }
  };

  useEffect(() => {
    if (status === "success" && !hasShownSuccess) {
      // ✅ Mark that we've shown success to prevent duplicates
      setHasShownSuccess(true);
      
      // ✅ Notify parent to refresh
      onFaqAdded?.();
      
      // ✅ Show success message ONCE
      successMessage("FAQ successfully added");
      
      // ✅ Invalidate cache
      queryClient.invalidateQueries({ queryKey });
      
      // ✅ Reset form
      setInputs({ question: "", answer: "" });
      setValidationErrors({});
      
      // ✅ Close offcanvas after short delay
      setTimeout(() => {
        toggleAddFAQ?.();
      }, 500);
      
      // ✅ Reset mutation status after handling
      setTimeout(() => {
        reset?.();
        setHasShownSuccess(false);
      }, 1000);
    }
    
    if (status === "error" && error) {
      const errorMsg = error?.response?.data?.message || error?.message || "Failed to add FAQ";
      dangerMessage(errorMsg);
      reset?.();
    }
  }, [status, error, queryClient, queryKey, onFaqAdded, toggleAddFAQ, reset, hasShownSuccess]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setInputs((prevInputs) => ({ ...prevInputs, [name]: value }));
    
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const isFormValid = inputs?.question?.trim() && inputs?.answer?.trim();
  const isDisabled = pending || !isFormValid;

  return (
    <div>
      <form
        noValidate
        autoComplete="off"
        data-testid="form"
        onSubmit={handleCreateFaq}
      >
        <Row>
          <Col md={12} lg={12}>
            <label className="d-flex justify-content-between align-items-center">
              <span>
                <span className="text-danger">*</span>Question
              </span>
              {validationErrors.question && (
                <span className="text-danger small">{validationErrors.question}</span>
              )}
            </label>
            <textarea
              name="question"
              value={inputs?.question || ""}
              onChange={handleChange}
              placeholder="Type your question here..."
              className={`mb-3 form-control fromInput ${
                validationErrors.question ? 'is-invalid' : ''
              }`}
              rows={3}
              style={{
                borderColor: validationErrors.question ? '#dc3545' : '',
                backgroundColor: validationErrors.question ? '#fff5f5' : ''
              }}
              disabled={pending}
            ></textarea>
            {validationErrors.question && (
              <div className="invalid-feedback d-block">
                {validationErrors.question}
              </div>
            )}
          </Col>
          
          <Col md={12} lg={12}>
            <label className="d-flex justify-content-between align-items-center">
              <span>
                <span className="text-danger">*</span>Answer
              </span>
              {validationErrors.answer && (
                <span className="text-danger small">{validationErrors.answer}</span>
              )}
            </label>
            <textarea
              name="answer"
              value={inputs?.answer || ""}
              onChange={handleChange}
              placeholder="Write here..."
              className={`mb-3 form-control fromInput ${
                validationErrors.answer ? 'is-invalid' : ''
              }`}
              rows={4}
              style={{
                borderColor: validationErrors.answer ? '#dc3545' : '',
                backgroundColor: validationErrors.answer ? '#fff5f5' : ''
              }}
              disabled={pending}
            ></textarea>
            {validationErrors.answer && (
              <div className="invalid-feedback d-block">
                {validationErrors.answer}
              </div>
            )}
          </Col>
          
          <Col md={12} lg={12}>
            <div className="w-100 mt-3 d-flex justify-content-center gap-2">
              <button
                type="submit"
                disabled={isDisabled}
                className="btn"
                style={{
                  backgroundColor: "#F68A20",
                  borderColor: "#F68A20",
                  color: "white",
                  borderRadius: "25px",
                  padding: "10px 40px",
                  fontWeight: "600",
                  fontSize: "14px",
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  opacity: isDisabled ? 0.6 : 1,
                  border: "2px solid #F68A20",
                  minWidth: "120px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                {pending ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Adding...
                  </>
                ) : (
                  "Add FAQ"
                )}
              </button>
              
              {toggleAddFAQ && (
                <button
                  type="button"
                  onClick={toggleAddFAQ}
                  className="btn btn-outline-secondary"
                  style={{
                    borderRadius: "25px",
                    padding: "10px 24px",
                    fontWeight: "500"
                  }}
                  disabled={pending}
                >
                  Cancel
                </button>
              )}
            </div>
          </Col>
        </Row>
      </form>
    </div>
  );
};

export default AddFAQ;